import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, forkJoin, map, mergeMap, of, switchMap } from 'rxjs';
import { CollectionsService } from '../service/collections.service';
import * as CollectionsActions from './collections.actions';

@Injectable()
export class CollectionsEffects {
  private actions$ = inject(Actions);
  private collectionsService = inject(CollectionsService);

  loadCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionsActions.loadCollections),
      switchMap(() =>
        this.collectionsService.getCollections().pipe(
          map((response) =>
            CollectionsActions.loadCollectionsSuccess({
              collections: response.collections ?? [],
              bookmarkCount: response.bookmark_count ?? null,
            }),
          ),
          catchError((error) => of(CollectionsActions.loadCollectionsFailure({ error }))),
        ),
      ),
    ),
  );

  createCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionsActions.createCollection),
      concatMap(({ name }) =>
        this.collectionsService.createCollection(name).pipe(
          map((response) =>
            CollectionsActions.createCollectionSuccess({
              collection: { id: response.collection.id, name: response.collection.name },
            }),
          ),
          catchError((error) => of(CollectionsActions.createCollectionFailure({ error }))),
        ),
      ),
    ),
  );

  renameCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionsActions.renameCollection),
      mergeMap(({ id, name, previousName }) =>
        this.collectionsService.renameCollection(id, name).pipe(
          map(() => CollectionsActions.renameCollectionSuccess({ id, name })),
          catchError((error) => of(CollectionsActions.renameCollectionFailure({ id, name, previousName, error }))),
        ),
      ),
    ),
  );

  deleteCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionsActions.deleteCollection),
      mergeMap(({ id }) =>
        this.collectionsService.deleteCollection(id).pipe(
          map(() => CollectionsActions.deleteCollectionSuccess({ id })),
          catchError((error) => of(CollectionsActions.deleteCollectionFailure({ id, error }))),
        ),
      ),
    ),
  );

  addDocumentsToCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionsActions.addDocumentsToCollection),
      mergeMap(({ collectionId, documentIds }) =>
        this.collectionsService.addDocuments(collectionId, documentIds).pipe(
          map(() => CollectionsActions.addDocumentsToCollectionSuccess({ collectionId, documentIds })),
          catchError((error) =>
            of(CollectionsActions.addDocumentsToCollectionFailure({ collectionId, documentIds, error })),
          ),
        ),
      ),
    ),
  );

  removeDocumentsFromCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionsActions.removeDocumentsFromCollections),
      mergeMap(({ removals }) => {
        const requests = removals
          .filter((removal) => removal.documentIds.length > 0)
          .map((removal) => this.collectionsService.removeDocuments(removal.collectionId, removal.documentIds));

        if (requests.length === 0) {
          return of(CollectionsActions.removeDocumentsFromCollectionsSuccess({ removals }));
        }

        return forkJoin(requests).pipe(
          map(() => CollectionsActions.removeDocumentsFromCollectionsSuccess({ removals })),
          catchError((error) => of(CollectionsActions.removeDocumentsFromCollectionsFailure({ removals, error }))),
        );
      }),
    ),
  );
}
